import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";
import { computeLightboxDimensions } from "../../lib/imageLightbox";

interface ImageLightboxProps {
  src: string | null;
  alt: string;
  open: boolean;
  onClose: () => void;
}

export function ImageLightbox({ src, alt, open, onClose }: ImageLightboxProps) {
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset state when opening/closing or src changes
  useEffect(() => {
    if (open && src) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setNaturalSize(null);

      const img = new Image();
      img.onload = () => setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
      img.src = src;
    }
  }, [open, src]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", handleKey, true);
    return () => window.removeEventListener("keydown", handleKey, true);
  }, [open, onClose]);

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => {
      const next = z + (e.deltaY < 0 ? 0.15 : -0.15);
      return Math.max(0.25, Math.min(8, next));
    });
  }, []);

  // Double click to toggle zoom
  const handleDoubleClick = useCallback(() => {
    if (zoom > 1.1) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } else {
      setZoom(2.5);
    }
  }, [zoom]);

  // Drag to pan (when zoomed)
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [zoom, pan]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    setPan({
      x: dragStart.current.panX + (e.clientX - dragStart.current.x),
      y: dragStart.current.panY + (e.clientY - dragStart.current.y),
    });
  }, [isDragging]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Click backdrop to close (only if not zoomed or not dragging)
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === containerRef.current && !isDragging && zoom <= 1.05) {
      onClose();
    }
  }, [onClose, isDragging, zoom]);

  // Compute dimensions
  const viewport = typeof window !== "undefined"
    ? { width: window.innerWidth, height: window.innerHeight }
    : { width: 1920, height: 1080 };

  const dims = naturalSize
    ? computeLightboxDimensions(naturalSize.w, naturalSize.h, viewport)
    : null;

  return (
    <AnimatePresence>
      {open && src && (
        <motion.div
          ref={containerRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          onClick={handleBackdropClick}
          onWheel={handleWheel}
          className={cn(
            "fixed inset-0 z-[100] flex items-center justify-center",
            "bg-black/80 backdrop-blur-[4px]",
            zoom > 1 ? "cursor-grab" : "cursor-zoom-in",
            isDragging && "cursor-grabbing",
          )}
        >
          {/* Close hint */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ delay: 0.1, duration: 0.15 }}
            className="absolute top-[var(--spacing-4)] right-[var(--spacing-4)] z-10"
          >
            <button
              onClick={onClose}
              className={cn(
                "flex items-center justify-center w-[36px] h-[36px]",
                "rounded-full bg-white/10 hover:bg-white/20",
                "text-white/80 hover:text-white",
                "transition-colors duration-[120ms] cursor-pointer",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40",
              )}
              aria-label="Close preview"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </motion.div>

          {/* Zoom indicator */}
          {zoom !== 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute bottom-[var(--spacing-4)] left-1/2 -translate-x-1/2 z-10 px-[var(--spacing-3)] py-[var(--spacing-1)] rounded-full bg-white/10 text-white/70 text-[12px] tabular-nums select-none"
            >
              {Math.round(zoom * 100)}%
            </motion.div>
          )}

          {/* Image */}
          {dims && (
            <motion.img
              src={src}
              alt={alt}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              style={{
                width: dims.width,
                height: dims.height,
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                transformOrigin: "center center",
              }}
              className={cn(
                "rounded-[var(--radius-sm)] select-none",
                "transition-[transform] duration-[80ms] ease-out",
                isDragging && "transition-none",
              )}
              draggable={false}
              onDoubleClick={handleDoubleClick}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
